import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { LogIn, FileText, Package, ArrowRight, Loader2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { products } from "../mock/mock";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const { user, loading, login } = useAuth();
  const [quotes, setQuotes] = useState([]);
  const [loadingQuotes, setLoadingQuotes] = useState(true);

  useEffect(() => {
    const fetchQuotes = async () => {
      try {
        const res = await axios.get(`${API}/my/quotes`, { withCredentials: true });
        setQuotes(res.data || []);
      } catch (e) {
        setQuotes([]);
      } finally {
        setLoadingQuotes(false);
      }
    };
    if (user) fetchQuotes();
    else setLoadingQuotes(false);
  }, [user]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#D80000] animate-spin" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-5">
        <span className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#D80000]/10 mb-5">
          <LogIn className="w-7 h-7 text-[#D80000]" />
        </span>
        <h1 className="font-heading text-3xl font-semibold text-[#1a1a1a]">Members Area</h1>
        <p className="text-[#1a1a1a]/70 mt-3 max-w-md">Sign in with Google to view your saved quote requests and manage your projects.</p>
        <button onClick={login} className="inline-flex items-center gap-2 mt-7 bg-[#D80000] text-white font-semibold px-7 py-3.5 rounded-md hover:bg-[#b00000]">
          <LogIn className="w-5 h-5" /> Sign in with Google
        </button>
      </div>
    );
  }

  const renderQuotes = () => {
    if (loadingQuotes) {
      return (
        <div className="py-10 flex justify-center">
          <Loader2 className="w-6 h-6 text-[#D80000] animate-spin" />
        </div>
      );
    }
    if (quotes.length === 0) {
      return (
        <div className="text-center py-12">
          <p className="text-[#1a1a1a]/60">You haven&apos;t submitted any quote requests yet.</p>
          <Link to="/contact" className="inline-flex items-center gap-2 mt-5 bg-[#D80000] text-white font-semibold px-6 py-3 rounded-md hover:bg-[#b00000]">
            Request a Quote <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      );
    }
    return (
      <ul className="divide-y">
        {quotes.map((q) => (
          <li key={q.id} className="py-4">
            <div className="flex items-center justify-between">
              <p className="font-semibold text-[#1a1a1a]">{q.interest || "General Enquiry"}</p>
              <span className="text-xs text-[#1a1a1a]/50">{new Date(q.created_at).toLocaleDateString()}</span>
            </div>
            <p className="text-sm text-[#1a1a1a]/70 mt-1 line-clamp-2">{q.message}</p>
          </li>
        ))}
      </ul>
    );
  };

  return (
    <div className="bg-[#f7f7f8] min-h-[70vh]">
      <section className="bg-[#141414] py-14">
        <div className="max-w-[1140px] mx-auto px-5 flex items-center gap-5">
          <img src={user.picture} alt={user.name} className="w-16 h-16 rounded-full border-2 border-[#D80000]" />
          <div>
            <p className="text-white/60 text-sm">Welcome back,</p>
            <h1 className="font-heading text-3xl font-bold text-white">{user.name}</h1>
          </div>
        </div>
      </section>

      <section className="max-w-[1140px] mx-auto px-5 py-12 grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 bg-white rounded-lg shadow-sm p-7">
          <div className="flex items-center gap-3 mb-6">
            <FileText className="w-5 h-5 text-[#D80000]" />
            <h2 className="font-heading text-xl font-semibold text-[#1a1a1a]">My Quote Requests</h2>
          </div>
          {renderQuotes()}
        </div>

        <div className="bg-white rounded-lg shadow-sm p-7">
          <div className="flex items-center gap-3 mb-6">
            <Package className="w-5 h-5 text-[#D80000]" />
            <h2 className="font-heading text-xl font-semibold text-[#1a1a1a]">Quick Links</h2>
          </div>
          <ul className="space-y-3">
            {products.slice(0, 5).map((p) => (
              <li key={p.slug}>
                <Link to={`/products/${p.slug}`} className="flex items-center justify-between text-[#1a1a1a]/80 hover:text-[#D80000] text-sm group">
                  {p.title}
                  <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </section>
    </div>
  );
};

export default Dashboard;
